import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import TrainingHistory from "./Training History/TrainingHistory";
import MyProgress from "./Current Training/Components/MyProgress";
import MobileHomeHeader from "./MobileHomeHeader";
import "./CSS/MobileHomePage.css";
import RestoreSessionList from "./Training History/RestoreSessionList";
import Achievements from "./Achievements";
import useBackSwipe from "../hooks/useBackSwipe";
import { animateBackAndRun } from "../hooks/useBackSwipe";
import {
  getCachedSessions,
  getCachedUserStats,
  setCachedSessions,
  setCachedUserStats,
} from "../offline/sessionOffline";

const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const MobileHomePage = ({ profile, userId, isMobile, setShowNameModal, showRestoreList, setShowRestoreList, onRestore, scrollContainerRef, setProfile, isOnline }) => {
  const [viewHistory, setViewHistory] = useState(false);
  const [totalSessions, setTotalSessions] = useState(0);
  const [displayedCount, setDisplayedCount] = useState(0);
  const [showAnalysisView, setShowAnalysisView] = useState(false); 
  const [showIncompletePopup, setShowIncompletePopup] = useState(false);
  const [lastIncompleteSession, setLastIncompleteSession] = useState(null);
  const [timeSpentMinutes, setTimeSpentMinutes] = useState(0);
  const [messagesSent, setMessagesSent] = useState(0);
  const [streakDays, setStreakDays] = useState(0);
  const mobileBackViewRef = useRef(null);
  const mobileUnderlayRef = useRef(null);
  const mobileUnderlayDimRef = useRef(null);

  useBackSwipe(
    () => {
      if (viewHistory && showAnalysisView) {
        setShowAnalysisView(false);
        return;
      }
      if (viewHistory) {
        setViewHistory(false);
        return;
      }
      if (showRestoreList) {
        setShowRestoreList(false);
      }
    },
    {
      enabled: (viewHistory && !showAnalysisView) || showRestoreList,
      target: mobileBackViewRef,
      underlay: mobileUnderlayRef,
      dimmer: mobileUnderlayDimRef,
    }
  );

  useEffect(() => {
  let start = 0;
  const duration = 1000;
  const increment = totalSessions / (duration / 16);
  let animationFrame;

  const animate = () => {
    start += increment;
    if (start < totalSessions) {
      setDisplayedCount(Math.floor(start));
      animationFrame = requestAnimationFrame(animate);
    } else {
      setDisplayedCount(totalSessions);
    }
  };

  animate();
  return () => cancelAnimationFrame(animationFrame);
}, [totalSessions]);

    
useEffect(() => {
  if ((viewHistory || showRestoreList) && scrollContainerRef?.current) {
    scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [viewHistory, showRestoreList]);

useEffect(() => {
  if (!userId) return;

  const cachedSessions = getCachedSessions(userId);
  if (cachedSessions?.sessions) {
    setTotalSessions(cachedSessions.sessions.length || 0);
  }

  const cachedStats = getCachedUserStats(userId);
  if (cachedStats?.stats) {
    setMessagesSent(cachedStats.stats.messagesSent || 0);
    setTimeSpentMinutes(cachedStats.stats.timeSpentMinutes || 0);
    setStreakDays(cachedStats.stats.streakDays || 0);
    if (typeof cachedStats.stats.totalSessions === "number") {
      setTotalSessions(cachedStats.stats.totalSessions);
    }
  }

  axios
    .get(`${REACT_APP_API_BASE_URL}/api/sessions/${userId}`)
    .then((res) => {
      const { sessions, latestIncompleteSession } = res.data;
      setTotalSessions(sessions.length);
      setCachedSessions(userId, sessions);

      if (latestIncompleteSession) {
        setLastIncompleteSession(latestIncompleteSession);
        setShowIncompletePopup(true);
      }
    })
    .catch((err) => console.error("Error fetching sessions:", err));

  axios
    .get(`${REACT_APP_API_BASE_URL}/api/user-stats/${userId}`)
    .then((res) => {
      const nextStats = {
        messagesSent: res.data.messagesSent || 0,
        timeSpentMinutes: res.data.timeSpentMinutes || 0,
        streakDays: res.data.streakDays || 0,
        totalSessions,
      };
      setMessagesSent(nextStats.messagesSent);
      setTimeSpentMinutes(nextStats.timeSpentMinutes);
      setStreakDays(nextStats.streakDays);
      setCachedUserStats(userId, nextStats);
    })
    .catch((err) => console.error("Error fetching user stats:", err));
}, [totalSessions, userId]);

return (
  <div className="mobile-dashboard-wrapper">
    {showIncompletePopup && lastIncompleteSession && !viewHistory && !showRestoreList && (
      <div className="incomplete-popup-overlay">
        <div className="incomplete-popup">
        <h4>Your last session wasn’t finished.</h4>
        <p>Do you want to pick up where you left off or start a new one?</p>
          <div className="popup-buttons">
            <button className="resume-btn" onClick={() => {
              onRestore(lastIncompleteSession);
              setShowIncompletePopup(false);
            }}>
              Continue Session
            </button>
            <button className="new-btn" onClick={() => setShowIncompletePopup(false)}>
              Start New
            </button>
          </div>
        </div>
      </div>
    )}

    <div className="mobile-underlay" ref={mobileUnderlayRef}>
      <div className="mobile-underlay-dim" ref={mobileUnderlayDimRef} />
      <MobileHomeHeader profile={profile} setShowNameModal={setShowNameModal} setProfile={setProfile} />

      <Achievements stats={{ 
      videos: totalSessions,
      minutes: timeSpentMinutes,
      messages: messagesSent,
      streak: streakDays
      }}/>

      <div className="mobile-homepage">
      <div className="welcome-card">
        <p className="welcome-tagline">Reflect, improve, and grow — one session at a time.</p>
        <p className="section-description">
          Review your parent-child interaction videos, reflect meaningfully, and get AI-powered insights to guide your next steps.
        </p>
      </div>

      <div className="card plot-card">
        <MyProgress userId={userId} totalSessions={displayedCount} timeSpent={timeSpentMinutes} messagesSent={messagesSent} streakDays={streakDays}/>
      </div>

      <div className="stats-section">
        <div className="history-card">
          <h3 className="section-title">Past Training</h3>
          <p className="section-description">
            View summaries and insights from your previous sessions.
          </p>
          <div className="button-row">
          <button
            className="history-button"
            onClick={() => setViewHistory(true)}
          >
            View Session Summary
          </button>
          <button
            className="history-button"
            onClick={() => setShowRestoreList(true)}
          >
            Restore Session
          </button>
           </div>
        </div>
      </div>
      </div>
    </div>

    {viewHistory && (
      <div
        className={`mobile-overlay-panel ${showAnalysisView ? "analysis-active" : ""}`}
        ref={mobileBackViewRef}
      >
        {!showAnalysisView && (
          <button
            className="back-button-training"
            onClick={() =>
              animateBackAndRun({
                target: mobileBackViewRef,
                underlay: mobileUnderlayRef,
                dimmer: mobileUnderlayDimRef,
                onBack: () => setViewHistory(false),
              })
            }
          >
            ← Back
          </button>
        )}
        <TrainingHistory
          userId={userId}
          isMobile={isMobile}
          isOnline={isOnline}
          showAnalysisView={showAnalysisView}
          setShowAnalysisView={setShowAnalysisView}
          onRestore={onRestore}
        />
      </div>
    )}

    {showRestoreList && (
      <div className="mobile-overlay-panel" ref={mobileBackViewRef}>
        <button
          className="back-button-training"
          onClick={() =>
            animateBackAndRun({
              target: mobileBackViewRef,
              underlay: mobileUnderlayRef,
              dimmer: mobileUnderlayDimRef,
              onBack: () => setShowRestoreList(false),
            })
          }
        >
          ← Back
        </button>
        <RestoreSessionList
          userId={userId}
          isMobile={isMobile}
          isOnline={isOnline}
          onRestore={(session) => {
            onRestore(session);
            setShowRestoreList(false);
          }}
        />
      </div>
    )}
  </div>
);
};

export default MobileHomePage;
